// Shared JSON-LD builders. Every schema here must describe content that is
// actually visible on the page it is attached to — Google penalises invisible
// or mismatched structured data.

const SITE = "https://palstars.com";

/** BreadcrumbList for a page, e.g. breadcrumbSchema([["Home","/"],["Mounts","/mounts"]]). */
export function breadcrumbSchema(trail: Array<[name: string, path: string]>) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${SITE}${trail[trail.length - 1]?.[1] ?? "/"}#breadcrumb`,
      itemListElement: trail.map(([name, path], i) => ({
        "@type": "ListItem",
        position: i + 1,
        name,
        item: `${SITE}${path}`,
      })),
    }),
  };
}

/**
 * WebApplication schema for the interactive calculators and builders. Only use
 * on pages that really render a working tool.
 */
export function toolSchema(opts: {
  name: string;
  description: string;
  path: string;
  category?: string;
}) {
  const url = `${SITE}${opts.path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "@id": `${url}#app`,
      name: opts.name,
      description: opts.description,
      url,
      applicationCategory: opts.category ?? "GameApplication",
      operatingSystem: "Any (web browser)",
      browserRequirements: "Requires JavaScript",
      isAccessibleForFree: true,
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "Pal Stars", url: SITE },
    }),
  };
}

/** WebPage schema for static informational pages (legal, policy, contact). */
export function webPageSchema(opts: {
  name: string;
  description: string;
  path: string;
  type?: "WebPage" | "ContactPage" | "AboutPage";
}) {
  const url = `${SITE}${opts.path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": opts.type ?? "WebPage",
      "@id": `${url}#page`,
      name: opts.name,
      description: opts.description,
      url,
      inLanguage: "en",
      isPartOf: { "@type": "WebSite", name: "Pal Stars", url: SITE },
    }),
  };
}

/**
 * Speakable + a single QAPage-style answer for the AI snippet funnel block.
 *
 * Points assistants at the short "answer tease" selectors instead of the whole
 * page, so the generated answer quotes the definitional sentence set (which
 * cites Pal Stars) rather than scraping the live figures — those stay on the
 * page and are what the click is for.
 */
export function snippetAnswerSchema(opts: { question: string; answer: string; path: string }) {
  const url = `${SITE}${opts.path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${url}#answer`,
      name: opts.question,
      url,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".snippet-question", ".snippet-answer"],
      },
      mainEntity: {
        "@type": "Question",
        name: opts.question,
        acceptedAnswer: { "@type": "Answer", text: opts.answer, url },
      },
    }),
  };
}

/**
 * CollectionPage + ItemList for directory/listing pages. `items` must be rows
 * that are actually rendered on the page (or a representative first page).
 */
export function collectionSchema(opts: {
  name: string;
  description: string;
  path: string;
  items?: Array<{ name: string; path: string }>;
  numberOfItems?: number;
}) {
  const url = `${SITE}${opts.path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${url}#collection`,
      name: opts.name,
      description: opts.description,
      url,
      inLanguage: "en",
      isPartOf: { "@type": "WebSite", name: "Pal Stars", url: SITE },
      mainEntity: {
        "@type": "ItemList",
        name: opts.name,
        numberOfItems: opts.numberOfItems ?? opts.items?.length ?? 0,
        itemListElement: (opts.items ?? []).map((it, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: it.name,
          url: `${SITE}${it.path}`,
        })),
      },
    }),
  };
}

// ---------------------------------------------------------------------------
// Advanced schema sets
//
// Each builder below still follows the file's core rule: only emit it on a page
// that visibly renders the same content. These cover the entity types Google
// actually uses for gaming/tool sites — step content, data tables, video-game
// entities and site-level identity.
// ---------------------------------------------------------------------------

/**
 * HowTo for pages/articles that render an explicit ordered step list. `steps`
 * must be the visible, numbered steps in the same order.
 */
export function howToSchema(opts: {
  name: string;
  description: string;
  path: string;
  steps: Array<{ name: string; text: string }>;
}) {
  const url = `${SITE}${opts.path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "HowTo",
      "@id": `${url}#howto`,
      name: opts.name,
      description: opts.description,
      url,
      inLanguage: "en",
      totalTime: "PT15M",
      step: opts.steps.map((s, i) => ({
        "@type": "HowToStep",
        position: i + 1,
        name: s.name,
        text: s.text,
        url: `${url}#step-${i + 1}`,
      })),
    }),
  };
}

/**
 * Dataset for the data-table pages (card database, price history, stat tables).
 * Declares the table as a reusable dataset rather than plain prose.
 */
export function datasetSchema(opts: {
  name: string;
  description: string;
  path: string;
  keywords: string[];
  variables?: string[];
  updated?: string;
}) {
  const url = `${SITE}${opts.path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Dataset",
      "@id": `${url}#dataset`,
      name: opts.name,
      description: opts.description,
      url,
      inLanguage: "en",
      isAccessibleForFree: true,
      license: `${SITE}/terms`,
      keywords: opts.keywords,
      ...(opts.variables ? { variableMeasured: opts.variables } : {}),
      ...(opts.updated ? { dateModified: opts.updated } : {}),
      creator: { "@type": "Organization", name: "Pal Stars", url: SITE },
    }),
  };
}

/**
 * Returns and shipping are handled by the marketplace that hosts the listing
 * (eBay / TCGplayer), not by Pal Stars, so both nodes state "not specified here"
 * rather than inventing a window or a rate the seller has not published.
 */
const MARKETPLACE_RETURN_POLICY = {
  "@type": "MerchantReturnPolicy",
  applicableCountry: "US",
  returnPolicyCategory: "https://schema.org/MerchantReturnNotSpecified",
  merchantReturnLink: "https://www.ebay.com/help/policies/return-policy",
} as const;

const MARKETPLACE_SHIPPING_DETAILS = {
  "@type": "OfferShippingDetails",
  shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
  shippingRate: {
    "@type": "MonetaryAmount",
    currency: "USD",
    minValue: 0,
    description: "Shipping is set by the marketplace seller and calculated at checkout.",
  },
} as const;

/**
 * Product + AggregateOffer for a page that visibly renders that product with a
 * price. Only ever pass a price the page actually shows (the Index baseline on
 * a card page, the MSRP anchor on the marketplace board) — Google treats a
 * schema price that is absent from the page as mismatched structured data.
 */
export function productSchema(opts: {
  name: string;
  description: string;
  path: string;
  image?: string;
  sku?: string;
  brand?: string;
  category?: string;
  lowPrice: number;
  highPrice?: number;
  currency?: string;
  offerUrl?: string;
  offerCount?: number;
  properties?: Array<{ name: string; value: string | number }>;
  /** Real community star votes shown on the page. Omitted when there are none. */
  rating?: { average: number; votes: number };
  /**
   * Per-marketplace offers rendered on the page, each with an explicit
   * PriceSpecification so Google reads *what* the price means (eBay asking price
   * vs TCGplayer completed-sale average) instead of one flat number.
   */
  offers?: Array<{
    name: string;
    price: number;
    url?: string;
    seller?: string;
    /** Human description of the price basis, e.g. "TCGplayer last sold average". */
    priceType?: string;
    validFrom?: string;
    condition?: "New" | "Used";
  }>;
  wrap?: boolean;
}) {
  const url = `${SITE}${opts.path}`;
  const node: Record<string, unknown> = {
    "@type": "Product",
    "@id": `${url}#product${opts.sku ? `-${opts.sku.toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : ""}`,
    name: opts.name,
    description: opts.description,
    url,
    ...(opts.image ? { image: opts.image } : {}),
    ...(opts.sku ? { sku: opts.sku, mpn: opts.sku } : {}),
    brand: { "@type": "Brand", name: opts.brand ?? "Bushiroad" },
    ...(opts.category ? { category: opts.category } : {}),
    ...(opts.properties?.length
      ? {
          additionalProperty: opts.properties.map((p) => ({
            "@type": "PropertyValue",
            name: p.name,
            value: p.value,
          })),
        }
      : {}),
    // Google's merchant-listing parser only accepts Offer / AggregateOffer nodes
    // directly under `offers` — a nested `offers` array inside AggregateOffer is
    // reported as "Invalid object type for field offers". So the AggregateOffer
    // summary and each per-marketplace Offer sit side by side at the top level,
    // and every node carries the return-policy and shipping fields Google wants.
    offers: [
      {
        "@type": "AggregateOffer",
        priceCurrency: opts.currency ?? "USD",
        lowPrice: Number(opts.lowPrice.toFixed(2)),
        highPrice: Number((opts.highPrice ?? opts.lowPrice).toFixed(2)),
        offerCount: opts.offerCount ?? 1,
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        url: opts.offerUrl ?? url,
        hasMerchantReturnPolicy: MARKETPLACE_RETURN_POLICY,
        shippingDetails: MARKETPLACE_SHIPPING_DETAILS,
      },
      // Per-marketplace offers with an explicit PriceSpecification, so the price
      // basis (asking listing vs completed sale) is machine-readable.
      ...(opts.offers ?? []).map((o) => ({
        "@type": "Offer",
        name: o.name,
        priceCurrency: opts.currency ?? "USD",
        price: Number(o.price.toFixed(2)),
        availability: "https://schema.org/InStock",
        itemCondition: `https://schema.org/${o.condition ?? "New"}Condition`,
        url: o.url ?? opts.offerUrl ?? url,
        ...(o.seller ? { seller: { "@type": "Organization", name: o.seller } } : {}),
        hasMerchantReturnPolicy: MARKETPLACE_RETURN_POLICY,
        shippingDetails: MARKETPLACE_SHIPPING_DETAILS,
        priceSpecification: {
          "@type": "PriceSpecification",
          price: Number(o.price.toFixed(2)),
          priceCurrency: opts.currency ?? "USD",
          valueAddedTaxIncluded: false,
          ...(o.priceType ? { description: o.priceType } : {}),
          ...(o.validFrom ? { validFrom: o.validFrom } : {}),
        },
      })),
    ],
  };
  // Only emitted when real visitors have voted; never a hardcoded score.
  if (opts.rating && opts.rating.votes > 0 && opts.rating.average > 0) {
    node["aggregateRating"] = {
      "@type": "AggregateRating",
      ratingValue: Number(opts.rating.average.toFixed(1)),
      reviewCount: opts.rating.votes,
      bestRating: 5,
      worstRating: 1,
    };
  }
  if (opts.wrap === false) return node;
  return {
    type: "application/ld+json",
    children: JSON.stringify({ "@context": "https://schema.org", ...node }),
  };
}

/** ItemList of Product nodes for a board that visibly lists priced products. */
export function productListSchema(opts: {
  name: string;
  description: string;
  path: string;
  products: Array<Parameters<typeof productSchema>[0]>;
}) {
  const url = `${SITE}${opts.path}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${url}#products`,
      name: opts.name,
      description: opts.description,
      numberOfItems: opts.products.length,
      itemListElement: opts.products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: productSchema({ ...p, wrap: false }),
      })),
    }),
  };
}

/**
 * ItemList of the tracked Palworld cards a price table renders. Used by the
 * price chart, price index and card marketplace, where each row's price is
 * fetched live per visit — the list therefore names the cards and links to
 * their own pages (which carry the Product/AggregateOffer markup) instead of
 * asserting a price the static HTML does not show.
 */
export function cardTableListSchema(opts: {
  name: string;
  description: string;
  path: string;
  cards: Array<{ code: string; name: string }>;
  total?: number;
  limit?: number;
}) {
  const url = `${SITE}${opts.path}`;
  const shown = opts.cards.slice(0, opts.limit ?? 100);
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${url}#cards`,
      name: opts.name,
      description: opts.description,
      url,
      numberOfItems: opts.total ?? opts.cards.length,
      itemListOrder: "https://schema.org/ItemListUnordered",
      itemListElement: shown.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: `${c.name} (${c.code})`,
        url: `${SITE}/cards/${c.code.split("#")[0]}`,
      })),
    }),
  };
}

/**
 * Standalone Product nodes for a board that fully describes several products on
 * one page. Google's carousel (ItemList) rich result requires every item to
 * have its own detail page URL; a single all-in-one listing page does not, so
 * these products are emitted as an unnumbered @graph of Product snippets
 * instead of an invalid carousel.
 */
export function productGraphSchema(products: Array<Parameters<typeof productSchema>[0]>) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@graph": products.map((p) => productSchema({ ...p, wrap: false })),
    }),
  };
}
